// ===== 날짜 유틸리티 함수 =====
import type { Worker } from '../services/types';

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 */
export function getTodayString(): string {
  const now = new Date();
  return formatToDateString(now);
}

/**
 * 이번 달을 YYYY-MM 형식으로 반환
 */
export function getMonthString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * YYYY-MM-DD 형식을 'M월 D일' 형식으로 변환
 */
export function formatDate(dateStr: string): string {
  const [, monthStr, dayStr] = dateStr.split('-');
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  return `${month}월 ${day}일`;
}

/**
 * ISO 문자열을 'M월 D일 HH:MM' 형식으로 변환
 */
export function formatDateTime(isoStr: string): string {
  const date = new Date(isoStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}월 ${day}일 ${hours}:${minutes}`;
}

/**
 * 해당 월의 일수 반환
 */
export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * 해당 월의 1일이 무슨 요일인지 반환 (0=일, 1=월, ..., 6=토)
 */
export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month - 1, 1).getDay();
}

/**
 * 주어진 날짜가 오늘인지 확인
 */
export function isToday(dateStr: string): boolean {
  return dateStr === getTodayString();
}

/**
 * 주어진 날짜가 해당 월에 속하는지 확인
 * @param dateStr YYYY-MM-DD
 * @param monthStr YYYY-MM
 */
export function isSameMonth(dateStr: string, monthStr: string): boolean {
  return normalizeDateString(dateStr).startsWith(monthStr);
}

/**
 * n일 전 날짜를 YYYY-MM-DD 형식으로 반환
 */
export function daysAgo(n: number): string {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return formatToDateString(date);
}

/**
 * 스프레드시트/Apps Script에서 넘어온 날짜 값을 YYYY-MM-DD로 정규화
 */
export function normalizeDateString(value: string | Date): string {
  if (value instanceof Date) return formatToDateString(value);
  const str = String(value || '');
  if (str.includes('T')) {
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) return formatToDateString(parsed);
    return str.split('T')[0];
  }
  return str.slice(0, 10);
}

export function formatDisplayDate(value?: string | Date): string {
  const normalized = normalizeDateString(value || '');
  if (!normalized) return '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const [year, month, day] = normalized.split('-');
  return `${year}.${month}.${day}`;
}

export function isWorkerEligibleForStatsOnDate(worker: Worker, dateStr: string): boolean {
  if (!worker) return false;
  const start = normalizeDateString(worker.employment_start_date || worker.created_at);
  const end = normalizeDateString(worker.employment_end_date || '');
  if (!start) return false;
  if (start > dateStr) return false;
  if (end && end < dateStr) return false;
  return true;
}

export function canWorkerLogin(worker: Worker): boolean {
  if (!worker) return false;
  return worker.status === 'active' && worker.employment_status === '취업 중';
}

// ===== 내부 헬퍼 =====

/**
 * Date 객체를 YYYY-MM-DD 문자열로 변환
 */
function formatToDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 잘못된 ISO 날짜 포맷이 연락처나 퇴사 사유 등에 매핑된 경우 빈 문자열로 정화
 */
export function sanitizeTextField(value?: string): string {
  const text = String(value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return '';
  return text;
}
